import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils/cn'

/**
 * Two-pane horizontal split with a draggable vertical divider.
 * Pane width is a percentage of the container, clamped to [minPct, 100-minPct].
 * Stateless across mounts; pass `storageKey` to persist to localStorage.
 */
export interface ResizableSplitProps {
  left: ReactNode
  right: ReactNode
  /** Default width of the LEFT pane, in percent. */
  defaultLeftPct?: number
  /** Minimum % for either side. */
  minPct?: number
  storageKey?: string
  className?: string
}

export function ResizableSplit({
  left, right,
  defaultLeftPct = 60,
  minPct = 20,
  storageKey,
  className,
}: ResizableSplitProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [leftPct, setLeftPct] = useState<number>(() => {
    if (typeof window === 'undefined' || !storageKey) return defaultLeftPct
    const raw = window.localStorage.getItem(storageKey)
    const n = raw ? Number(raw) : NaN
    return Number.isFinite(n) && n >= minPct && n <= 100 - minPct ? n : defaultLeftPct
  })
  const [dragging, setDragging] = useState(false)

  useEffect(() => {
    if (!storageKey) return
    try { window.localStorage.setItem(storageKey, String(leftPct)) } catch { /* ignore */ }
  }, [leftPct, storageKey])

  const onMouseDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    setDragging(true)
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
  }, [])

  useEffect(() => {
    if (!dragging) return
    const onMove = (e: PointerEvent) => {
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      // Account for RTL: pane order in DOM is left then right, so x-offset from
      // container start always maps to the "left" pane regardless of writing direction.
      const rtl = getComputedStyle(el).direction === 'rtl'
      const offset = rtl ? rect.right - e.clientX : e.clientX - rect.left
      let pct = (offset / rect.width) * 100
      pct = Math.max(minPct, Math.min(100 - minPct, pct))
      setLeftPct(pct)
    }
    const onUp = () => setDragging(false)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [dragging, minPct])

  return (
    <div
      ref={containerRef}
      className={cn('flex w-full h-full overflow-hidden', dragging && 'select-none cursor-col-resize', className)}
    >
      <div style={{ width: `${leftPct}%` }} className="overflow-hidden flex flex-col min-w-0">
        {left}
      </div>
      <div
        role="separator"
        aria-orientation="vertical"
        onPointerDown={onMouseDown}
        className={cn(
          'w-1.5 shrink-0 bg-border hover:bg-primary/60 transition-colors cursor-col-resize',
          dragging && 'bg-primary',
        )}
      />
      <div style={{ width: `${100 - leftPct}%` }} className="overflow-hidden flex flex-col min-w-0">
        {right}
      </div>
    </div>
  )
}
